import * as MD from '@material-ui/core';
import * as React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { Dispatch } from 'redux';
import { IApplicationState, IConnectedReduxProps } from '../../../Store';

import { I18N } from '../../../Store/I18n/Types';
import {
	getACLResource,
	getACLResourcesParents,
	getACLResourcesTypes,
	updateACLResource,
} from '../../../Store/ResourcesManagement/Actions';
import {
	ACLResource,
	IResourcesManagementState,
} from '../../../Store/ResourcesManagement/Types';
import { IUserState } from '../../../Store/User/Types';
import { getTheme } from '../../../Themes';
import { isNull } from '../../../Utils/Various';

export interface IRouterMatchParams {
	groupName: string;
	type: string;
}

interface IPropsFromState {
	i18n: I18N;
	router: RouteComponentProps<IRouterMatchParams>;
	user: IUserState;
	resourcesManagement: IResourcesManagementState;
}

interface IState {
	resourceUpdated: ACLResource | null;
}

interface IPropsFromDispatch {
	getACLResource: typeof getACLResource;
	getACLResourcesParents: typeof getACLResourcesParents;
	getACLResourcesTypes: typeof getACLResourcesTypes;
	updateACLResource: typeof updateACLResource;
}

const theme = getTheme();

const styles = MD.createStyles({
	row: {
		'& > *': {
			borderBottom: 'unset',
		},
	},
	margin: {
		margin: theme.spacing(1),
	},
	noResult: {
		textAlign: 'center',
		fontSize: 'large',
	},
	smallIcon: {
		padding: '3px',
	},
	cell5: {
		width: '5%',
	},
	cell125: {
		width: '12.5%',
	},
	cell15: {
		width: '15%',
	},
	cell20: {
		width: '20%',
	},
	flexContainer: {
		display: 'flex',
		alignItems: 'center',
		boxSizing: 'border-box',
	},
	tableRow: {
		cursor: 'pointer',
	},
	tableRowHover: {
		'&:hover': {
			backgroundColor: theme.palette.grey[200],
		},
	},
	tableCell: {
		flex: 1,
	},
	noClick: {
		cursor: 'initial',
	},
	card: {
		maxWidth: '55%',
		minWidth: '20%',
		margin: 'auto',
		marginTop: '3%',
	},
	cardContent: {
		width: '100%',
	},
	form: {
		display: 'flex',
		flexDirection: 'column',
		flexWrap: 'wrap',
	},
	label: {
		paddingTop: '15px',
	},
});

type AllProps = MD.WithStyles<typeof styles> &
	IPropsFromState &
	IPropsFromDispatch &
	RouteComponentProps<{}> &
	IConnectedReduxProps;

export class ResourceComponent extends React.Component<AllProps, IState> {
	public constructor(props: AllProps) {
		super(props);
		this.state = {
			resourceUpdated: null,
		};
	}

	public componentDidMount() {
		this.props.getACLResourcesParents(this.props.user.token);
		this.props.getACLResourcesTypes(this.props.user.token);
		if (this.props.router.match.params.type !== undefined) {
			if (this.props.router.match.params.type !== 'undefined') {
				this.props.getACLResource(
					this.props.user.token,
					parseInt(this.props.router.match.params.type, 10)
				);
			}
		}
	}

	public componentDidUpdate(prevProps: AllProps, prevState: IState) {
		if (prevProps !== this.props) {
			if (
				!isNull(this.props.resourcesManagement.aclResource) &&
				isNull(this.state.resourceUpdated)
			) {
				this.setState({
					resourceUpdated: this.props.resourcesManagement.aclResource,
				});
			}
		}
	}

	componentWillUnmount() { }

	public render(): React.ReactNode {
		const { classes } = this.props;
		return (
			<React.Fragment>
				<MD.Card className={classes.card}>
					<MD.CardActionArea></MD.CardActionArea>
					<MD.CardActions>
						<MD.CardContent className={classes.cardContent}>
							<MD.Typography
								variant="caption"
								color="textSecondary"
								component="p"
							></MD.Typography>
							{this.getForm()}
						</MD.CardContent>
					</MD.CardActions>
				</MD.Card>
			</React.Fragment>
		);
	}

	private getForm(): React.ReactNode {
		const { classes } = this.props;
		if (
			isNull(this.state.resourceUpdated) ||
			this.state.resourceUpdated === undefined
		) {
			return <div>empty resourceUpdated</div>;
		} else {
			return (
				<form
					className={classes.form}
					noValidate={true}
					onSubmit={this.onSubmit.bind(this)}
				>
					<MD.InputLabel className={classes.label}>
						{this.props.i18n._('Name')}
					</MD.InputLabel>
					<MD.TextField
						margin="normal"
						value={this.state.resourceUpdated.name}
						onChange={(event: any) => {
							this.setState({
								resourceUpdated: {
									...(this.state
										.resourceUpdated as ACLResource),
									name: event.target.value as string,
								},
							});
						}}
					/>
					<MD.Button
						color="primary"
						variant="contained"
						onClick={this.onSubmit.bind(this)}
					>
						{this.props.i18n._('Update')}
					</MD.Button>

					<MD.Button
						color="secondary"
						variant="contained"
						onClick={this.onReset.bind(this)}
					>
						{this.props.i18n._('Reset Form')}
					</MD.Button>
				</form>
			);
		}
	}

	private onSubmit() {
		if (
			!isNull(this.props.resourcesManagement.aclResource) &&
			!isNull(this.state.resourceUpdated)
		) {
			this.props.updateACLResource(
				this.props.user.token,
				this.props.resourcesManagement.aclResource.id,
				this.state.resourceUpdated
			);
		}
	}

	private onReset() {
		this.setState({
			resourceUpdated: this.props.resourcesManagement.aclResource,
		});
	}
}

const mapStateToProps = (
	{ i18n, user, resourcesManagement }: IApplicationState,
	ownProps: RouteComponentProps<IRouterMatchParams>
) => ({
	i18n: i18n.i18n,
	router: ownProps,
	user,
	resourcesManagement,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	getACLResource: (token: string, id: number) =>
		dispatch(getACLResource(token, id)),
	getACLResourcesParents: (token: string) =>
		dispatch(getACLResourcesParents(token)),
	getACLResourcesTypes: (token: string) =>
		dispatch(getACLResourcesTypes(token)),
	updateACLResource: (token: string, id: number, aclResource: ACLResource) =>
		dispatch(updateACLResource(token, id, aclResource)),
});

export const Resource = connect(
	mapStateToProps,
	mapDispatchToProps
)(MD.withStyles(styles)(ResourceComponent));
